import { Seo } from "../components/Seo";
import { CtaBand, Eyebrow } from "../components/Blocks";
import { Link, returnToPreviousPage, useNavigate } from "../components/Layout";

export default function GuideFieldTrip() {
  const navigate = useNavigate();

  return <main>
    <Seo path="/guide/field-trip-checklist" />
    <section className="legal guide-page">
      <nav className="guide-breadcrumb" aria-label="현재 위치">
        <Link href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>어린이집·유치원 단체관람</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">장소 고르는 법</span>
      </nav>
      <Eyebrow>GUIDE</Eyebrow>
      <h1>어린이집 현장학습 장소,<br />무엇을 확인하고 고르시나요</h1>
      <p>장소를 정하는 일은 프로그램이 좋은지를 고르는 일이 아니라, 당일 아이들이 무리 없이 다녀올 수 있는지를 확인하는 일에 가깝습니다. 실제 운영에서 문제가 생기는 지점을 순서대로 정리했습니다.</p>

      <article className="guide-content">
        <section>
          <h2>1. 이동 시간이 프로그램 시간보다 중요합니다</h2>
          <p>영유아 현장학습에서 가장 자주 어긋나는 것은 프로그램이 아니라 이동입니다. 편도 이동이 40분을 넘어가면 도착 시점에 아이들의 집중력이 이미 소진되어 있고, 돌아오는 길에는 점심·낮잠 일정이 밀립니다.</p>
          <p>후보지를 비교할 때 <b>출발–도착–프로그램–귀원</b>을 하나의 타임라인으로 그려 보시면, 프로그램 자체의 매력보다 총 소요 시간이 결정적이라는 점이 드러납니다.</p>
        </section>

        <section>
          <h2>2. 하차 지점과 주차를 미리 확인합니다</h2>
          <p>전세버스는 지하주차장 높이 제한에 걸리는 경우가 많습니다. 확인할 것은 세 가지입니다.</p>
          <ul>
            <li>버스가 아이들을 내려줄 수 있는 지점이 건물 앞인지, 떨어진 곳인지</li>
            <li>하차 지점에서 입구까지 인솔 동선에 횡단보도나 차량 통행이 있는지</li>
            <li>공연·체험 시간 동안 버스가 대기할 수 있는지, 따로 이동해야 하는지</li>
          </ul>
          <p>예약 전에 "우리 버스는 몇 인승이고 몇 시에 도착합니다"라고 알리면, 대부분의 시설이 하차 동선을 미리 정리해 줍니다.</p>
        </section>

        <section>
          <h2>3. 한 기관이 단독으로 보는지, 두 기관이 함께 보는지</h2>
          <p>같은 프로그램이라도 단독 운영과 합동 운영은 경험이 크게 다릅니다. 여러 기관이 섞이면 인원이 늘어 진행이 일방적으로 흐르고, 아이가 반응했을 때 제지당하기 쉽습니다. 인솔 교사 입장에서도 우리 반 아이들을 찾는 데 신경이 분산됩니다.</p>
          <p>단독 운영이면 시작 시간과 진행 속도를 우리 원 아이들의 상태에 맞출 수 있습니다.</p>
        </section>

        <section>
          <h2>4. 아이가 반응했을 때 어떻게 하는 곳인지</h2>
          <p>영유아 대상 프로그램에서 실제로 갈리는 지점입니다. 아이가 울거나 일어서거나 질문하는 것은 예외 상황이 아니라 기본값입니다.</p>
          <p>문의할 때 이렇게 물어보시면 답이 분명해집니다. <b>"아이가 중간에 울거나 돌아다니면 어떻게 하시나요?"</b> 조용히 시켜 달라는 요청이 돌아오는 곳과, 그것을 전제로 진행이 설계된 곳은 당일 교사의 부담이 완전히 다릅니다.</p>
        </section>

        <section>
          <h2>5. 연령과 집중 시간에 맞는 길이인지</h2>
          <p>프로그램 길이는 아이의 연령에 따라 판단해야 합니다. 만 3세와 만 5세는 앉아 있을 수 있는 시간이 다르고, 혼합 연령이면 어린 쪽에 맞춰야 전체가 편안합니다.</p>
          <p>연령별로 무엇이 달라지는지는 <Link className="inline-link" href="/guide/first-classic-age" navigate={navigate}>영유아 클래식 공연, 몇 살부터 괜찮을까</Link>에서 더 자세히 정리했습니다.</p>
        </section>

        <section>
          <h2>6. 비용이 인원 변동을 반영하는지</h2>
          <p>당일 결석은 반드시 생깁니다. 예약 인원 기준으로 정산하는 곳과 실제 방문 인원으로 정산하는 곳은 결제 금액이 달라집니다. 확인할 것은 두 가지입니다.</p>
          <ul>
            <li>결석분이 제외되는지, 예약 인원으로 고정되는지</li>
            <li>인솔 교사가 인원에 포함되는지</li>
          </ul>
        </section>

        <section>
          <h2>7. 서류 처리가 되는 곳인지</h2>
          <p>기관 회계 처리에 필요한 서류는 원마다 다릅니다. 견적서, 사업자등록증 사본, 세금계산서, 안전 관련 확인서 등 필요한 서류를 문의 단계에서 미리 알려 두면 결제 직전에 일정이 막히는 일을 피할 수 있습니다.</p>
        </section>

        <section>
          <h2>정리하면</h2>
          <p>이동 시간, 하차 동선, 단독 운영 여부, 아이의 반응을 다루는 방식, 연령에 맞는 길이, 인원 정산 기준, 서류. 이 일곱 가지를 문의 단계에서 확인하면 당일에 생길 문제의 대부분이 사라집니다.</p>
          <p>마이리틀클래식의 <Link className="inline-link" href="/venue" navigate={navigate}>동탄 공연장</Link>은 어린이집·유치원 단체 예약제로 운영하며, 한 기관 단독 방문과 두 기관 공동 방문이 모두 가능합니다. 차량은 건물 앞에서 바로 하차하고 공연장까지 저희가 길안내를 해 드리며, 공연 45분과 악기 체험 15분 내외로 구성됩니다. 관람료는 공연 당일 실제 방문한 원생 수를 기준으로 정산하고 인솔 교사는 인원에서 제외합니다. 회차별 관람 기록은 <Link className="inline-link" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>단체관람 페이지</Link>에서 보실 수 있습니다.</p>
        </section>
      </article>

      <nav className="guide-return" aria-label="단체관람 안내로 돌아가기">
        <div><span>GROUP VISIT</span><strong>어린이집·유치원 단체관람 안내</strong></div>
        <Link className="button ghost" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>단체관람 안내로 돌아가기 <span aria-hidden="true">→</span></Link>
      </nav>
    </section>

    <CtaBand
      title="현장학습 일정을 검토 중이시라면"
      body="희망 방문일과 아이들의 연령, 인원을 알려주시면 가능한 일정과 소요 시간을 정리해 안내드립니다."
      type="visit"
    />
  </main>;
}
